import { Input } from '@heroui/react'
import React from 'react'
import type { RadarConfig } from '../../../../types'
import { SavedCheckmark } from '../../pages/Admin/usePersistedConfig'
import { RichTextEditor } from './RichTextEditor'

interface ContentSettingsProps {
  config: RadarConfig | null
  setConfig: (config: RadarConfig) => void
}

export const ContentSettings = ({
  config,
  setConfig
}: ContentSettingsProps) => {
  if (!config) return null

  // Update functions now only update the config in memory
  const updateQuadrant = (
    id: string,
    field: string,
    value: string | number
  ) => {
    const quadrantIndex = config.quadrants.findIndex(q => q.id === id)
    if (quadrantIndex === -1) return
    const updatedQuadrant = {
      ...config.quadrants[quadrantIndex],
      [field]: value
    }
    const newQuadrants = [...config.quadrants]
    newQuadrants[quadrantIndex] = updatedQuadrant
    setConfig({ ...config, quadrants: newQuadrants })
  }

  const updateRing = (id: string, field: string, value: string | number) => {
    const ringIndex = config.rings.findIndex(r => r.id === id)
    if (ringIndex === -1) return
    const updatedRing = {
      ...config.rings[ringIndex],
      [field]: value
    }
    const newRings = [...config.rings]
    newRings[ringIndex] = updatedRing
    setConfig({ ...config, rings: newRings })
  }

  const updateFlag = (key: string, field: string, value: string) => {
    setConfig({
      ...config,
      flags: {
        ...config.flags,
        [key]: {
          ...config.flags[key as keyof typeof config.flags],
          [field]: value
        }
      }
    })
  }

  const updateLabel = (key: keyof RadarConfig['labels'], value: string) => {
    setConfig({
      ...config,
      labels: {
        ...config.labels,
        [key]: value
      }
    })
  }

  // State for saved status per quadrant/field
  const [quadrantSaved, setQuadrantSaved] = React.useState<{
    [qId: string]: { title?: boolean; description?: boolean }
  }>({})
  // State for saved status per ring title
  const [ringSaved, setRingSaved] = React.useState<{ [rId: string]: boolean }>(
    {}
  )
  // State for saved status per flag title
  const [flagSaved, setFlagSaved] = React.useState<{ [fId: string]: boolean }>(
    {}
  )
  // State for saved status per label
  const [labelSaved, setLabelSaved] = React.useState<{
    [lKey: string]: boolean
  }>({})
  // State for rich text editor values and saved checkmarks
  const [aboutContent, setAboutContent] = React.useState(
    config.labels?.pageAboutContent || ''
  )
  const [aboutContentSaved, setAboutContentSaved] = React.useState(false)
  const [footerContent, setFooterContent] = React.useState(
    config.labels?.footer || ''
  )
  const [footerSaved, setFooterSaved] = React.useState(false)

  // Handler for blur event on title/description
  const handleQuadrantBlur = (
    qId: string,
    field: 'title' | 'description',
    value: string
  ) => {
    updateQuadrant(qId, field, value)
    setQuadrantSaved(prev => ({
      ...prev,
      [qId]: {
        ...prev[qId],
        [field]: true
      }
    }))
    setTimeout(() => {
      setQuadrantSaved(prev => ({
        ...prev,
        [qId]: { ...prev[qId], [field]: false }
      }))
    }, 1200)
  }
  // Handler for blur event on ring title
  const handleRingTitleBlur = (rId: string, value: string) => {
    updateRing(rId, 'title', value)
    setRingSaved(prev => ({ ...prev, [rId]: true }))
    setTimeout(() => {
      setRingSaved(prev => ({ ...prev, [rId]: false }))
    }, 1200)
  }
  // Handler for blur event on flag title
  const handleFlagTitleBlur = (fId: string, value: string) => {
    updateFlag(fId, 'title', value)
    setFlagSaved(prev => ({ ...prev, [fId]: true }))
    setTimeout(() => {
      setFlagSaved(prev => ({ ...prev, [fId]: false }))
    }, 1200)
  }
  // Handler for blur event on label
  const handleLabelBlur = (lKey: string, value: string) => {
    updateLabel(lKey as keyof RadarConfig['labels'], value)
    setLabelSaved(prev => ({ ...prev, [lKey]: true }))
    setTimeout(() => {
      setLabelSaved(prev => ({ ...prev, [lKey]: false }))
    }, 1200)
  }

  // Handler for blur event on RichTextEditor
  const handleRichTextBlur = (
    key: keyof RadarConfig['labels'],
    value: string,
    setSaved: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    updateLabel(key, value)
    setSaved(true)
    setTimeout(() => setSaved(false), 1200)
  }

  return (
    <div className="space-y-6" data-testid="content-settings">
      <h3
        className="text-lg font-medium text-[var(--ds-text)] mb-4"
        data-testid="content-settings-title"
      >
        Content Settings
      </h3>
      <div className="space-y-6">
        <div>
          <h4
            className="text-sm font-medium text-[var(--ds-text)] mb-2"
            data-testid="quadrants-title"
          >
            Quadrant Details
          </h4>
          <div className="grid grid-cols-2 gap-4" data-testid="quadrants-grid">
            {config.quadrants.map(quadrant => (
              <div
                key={quadrant.id}
                className="flex items-center gap-2 p-3 border border-[var(--ds-border)] rounded-[3px]"
                data-testid={`quadrant-${quadrant.id}`}
              >
                <input
                  type="color"
                  value={quadrant.color}
                  onChange={e =>
                    updateQuadrant(quadrant.id, 'color', e.target.value)
                  }
                  className="w-8 h-8 rounded-xs cursor-pointer"
                  data-testid={`quadrant-color-${quadrant.id}`}
                  aria-label={`Color for ${quadrant.title} quadrant`}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1">
                    <div
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '100%'
                      }}
                    >
                      <Input
                        defaultValue={quadrant.title}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur()
                          }
                        }}
                        onBlur={e =>
                          handleQuadrantBlur(
                            quadrant.id,
                            'title',
                            e.target.value
                          )
                        }
                        data-testid={`quadrant-title-${quadrant.id}`}
                        aria-label={`Title for quadrant ${quadrant.id}`}
                        classNames={{
                          input:
                            'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none pr-5', // reduced right padding
                          inputWrapper:
                            'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-8 data-[focused=true]:border-[var(--ds-border-focused)]'
                        }}
                      />
                      <SavedCheckmark
                        isSaved={!!quadrantSaved[quadrant.id]?.title}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <div
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '100%'
                      }}
                    >
                      <Input
                        placeholder="Description"
                        data-testid={`quadrant-description-${quadrant.id}`}
                        defaultValue={quadrant.description}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur()
                          }
                        }}
                        onBlur={e =>
                          handleQuadrantBlur(
                            quadrant.id,
                            'description',
                            e.target.value
                          )
                        }
                        aria-label={`Description for ${quadrant.title} quadrant`}
                        classNames={{
                          input:
                            'text-sm group-data-[has-value=true]:text-[var(--ds-text-subtle)] outline-none pr-5', // reduced right padding
                          inputWrapper:
                            'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-8 data-[focused=true]:border-[var(--ds-border-focused)]'
                        }}
                      />
                      <SavedCheckmark
                        isSaved={!!quadrantSaved[quadrant.id]?.description}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4
            className="text-sm font-medium text-[var(--ds-text)] mb-2"
            data-testid="rings-title"
          >
            Rings
          </h4>
          <div className="grid grid-cols-2 gap-4" data-testid="rings-grid">
            {config.rings.map(ring => (
              <div
                key={ring.id}
                className="flex items-center gap-2 p-3 border border-[var(--ds-border)] rounded-[3px]"
                data-testid={`ring-${ring.id}`}
              >
                <input
                  type="color"
                  value={ring.color}
                  onChange={e => updateRing(ring.id, 'color', e.target.value)}
                  className="w-8 h-8 rounded-xs cursor-pointer"
                  data-testid={`ring-color-${ring.id}`}
                  aria-label={`Color for ${ring.title} ring-3`}
                />
                <div className="flex-1 mr-4">
                  <div className="flex items-center gap-1">
                    <div
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '100%'
                      }}
                    >
                      <Input
                        defaultValue={ring.title}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur()
                          }
                        }}
                        onBlur={e =>
                          handleRingTitleBlur(ring.id, e.target.value)
                        }
                        data-testid={`ring-title-${ring.id}`}
                        aria-label={`Title for ring-3 ${ring.id}`}
                        classNames={{
                          input:
                            'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none pr-5',
                          inputWrapper:
                            'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-8 data-[focused=true]:border-[var(--ds-border-focused)]'
                        }}
                      />
                      <SavedCheckmark isSaved={!!ringSaved[ring.id]} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 min-w-[100px]">
                  <label
                    htmlFor={`ring-radius-${ring.id}`}
                    className="text-xs text-[var(--ds-text-subtle)]"
                    data-testid={`ring-radius-label-${ring.id}`}
                  >
                    Radius:
                  </label>
                  <Input
                    id={`ring-radius-${ring.id}`}
                    type="number"
                    min={0}
                    max={1}
                    step={0.1}
                    value={ring.radius.toString()}
                    onChange={e =>
                      updateRing(
                        ring.id,
                        'radius',
                        Number.parseFloat(e.target.value)
                      )
                    }
                    data-testid={`ring-radius-${ring.id}`}
                    aria-label={`Radius for ${ring.title} ring-3`}
                    classNames={{
                      input:
                        'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none',
                      inputWrapper:
                        'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-8 data-[focused=true]:border-[var(--ds-border-focused)]'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4
            className="text-sm font-medium text-[var(--ds-text)] mb-2"
            data-testid="flags-title"
          >
            Status Flags
          </h4>
          <div className="grid grid-cols-2 gap-4" data-testid="flags-grid">
            {Object.entries(config.flags).map(([key, flag]) => (
              <div
                key={key}
                className="flex items-center gap-2 p-3 border border-[var(--ds-border)] rounded-[3px]"
                data-testid={`flag-${key}`}
              >
                <input
                  type="color"
                  value={flag.color}
                  onChange={e => updateFlag(key, 'color', e.target.value)}
                  className="w-8 h-8 rounded-xs cursor-pointer"
                  data-testid={`flag-color-${key}`}
                  aria-label={`Color for ${flag.title} status flag`}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1">
                    <div
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '100%'
                      }}
                    >
                      <Input
                        defaultValue={flag.title}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur()
                          }
                        }}
                        onBlur={e => handleFlagTitleBlur(key, e.target.value)}
                        data-testid={`flag-title-${key}`}
                        aria-label={`Title for ${flag.title} status flag`}
                        classNames={{
                          input:
                            'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none pr-5',
                          inputWrapper:
                            'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-8 data-[focused=true]:border-[var(--ds-border-focused)]'
                        }}
                      />
                      <SavedCheckmark isSaved={!!flagSaved[key]} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4
            className="text-sm font-medium text-[var(--ds-text)] mb-2"
            data-testid="labels-title"
          >
            Labels
          </h4>
          <div className="grid grid-cols-2 gap-4" data-testid="labels-grid">
            {Object.entries(config.labels)
              .filter(
                ([key]) =>
                  key !== 'metaDescription' &&
                  key !== 'pageAboutContent' &&
                  key !== 'footer'
              )
              .map(([key, value]) => (
                <div
                  key={key}
                  className={`flex items-center gap-2 p-3 border border-[var(--ds-border)] rounded-[3px] ${key === 'pageAbout' ? 'col-span-1' : ''}`}
                  data-testid={`label-${key}`}
                >
                  <div className="flex-1 flex items-center gap-1">
                    <label
                      htmlFor={`label-input-${key}`}
                      className="text-sm text-[var(--ds-text-subtle)] mb-1 capitalize block min-w-[120px] w-[150px]"
                      data-testid={`label-title-${key}`}
                    >
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </label>
                    <div
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '100%'
                      }}
                    >
                      <Input
                        id={`label-input-${key}`}
                        defaultValue={value}
                        onBlur={e => handleLabelBlur(key, e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur()
                          }
                        }}
                        data-testid={`label-input-${key}`}
                        aria-label={`${key.replace(/([A-Z])/g, ' $1').trim()} label`}
                        classNames={{
                          input:
                            'text-sm group-data-[has-value=true]:text-[var(--ds-text)] outline-none pr-5',
                          inputWrapper:
                            'border bg-[var(--ds-surface)] data-[hover=true]:bg-[var(--ds-surface)] focus-within:bg-[var(--ds-surface)] rounded-[3px] h-8 data-[focused=true]:border-[var(--ds-border-focused)]'
                        }}
                      />
                      <SavedCheckmark isSaved={!!labelSaved[key]} />
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div>
          <h4
            className="text-sm font-medium text-[var(--ds-text)] mb-2"
            data-testid="about-content-title"
          >
            About Page Content
          </h4>
          <div className="flex items-center gap-1 mt-2">
            <RichTextEditor
              value={aboutContent}
              onChange={setAboutContent}
              onBlur={() =>
                handleRichTextBlur(
                  'pageAboutContent',
                  aboutContent,
                  setAboutContentSaved
                )
              }
              minHeight="10rem"
              dataTestId="about-content"
              ariaLabel="About page content"
              placeholder="Enter content for the About page..."
            />
            <SavedCheckmark isSaved={aboutContentSaved} />
          </div>
          <div
            className="mt-2 text-xs text-[var(--ds-text-subtle)]"
            data-testid="about-content-help"
            role="note"
          >
            HTML is supported. Use tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;,
            &lt;li&gt;, &lt;strong&gt;, etc. for formatting.
          </div>
        </div>

        <div>
          <h4
            className="text-sm font-medium text-[var(--ds-text)] mb-2"
            data-testid="footer-content-title"
          >
            Footer Content
          </h4>
          <div className="flex items-center gap-1 mt-2">
            <RichTextEditor
              value={footerContent}
              onChange={setFooterContent}
              onBlur={() =>
                handleRichTextBlur('footer', footerContent, setFooterSaved)
              }
              minHeight="3.5rem"
              dataTestId="footer-content"
              ariaLabel="Footer content"
              placeholder="Enter content for the footer..."
            />
            <SavedCheckmark isSaved={footerSaved} />
          </div>
          <div
            className="mt-2 text-xs text-[var(--ds-text-subtle)]"
            data-testid="footer-content-help"
            role="note"
          >
            Customize the text displayed in the footer section of your radar.
          </div>
        </div>
      </div>
    </div>
  )
}
